import { PLAN_TYPES } from 'containers/App/constants'

import {
  atLeastCampaignsOnlyPlan,
  atLeastIronscalesProtectPlan,
  atLeastStarterPlan,
  exactStarterPlan,
} from './plans'

/* Permissions hierarchy
 *
 * admin (1)
 * manage_irontraps (2) Incident Manager
 * irontraps_action (5) Incident Operator
 * irontraps_viewer (4) Incident Viewer
 * edit_campaigns (3) Campaign Editor
 *
 */
const ROLE = {
  ADMIN: 'admin',
  INCIDENT_MANAGER: 'manage_irontraps',
  CAMPAIGN_EDITOR: 'edit_campaigns',
  INCIDENT_VIEWER: 'irontraps_viewer',
  INCIDENT_OPERATOR: 'irontraps_action',
}

/**
 * If section has the 'self' rules: each child
 * on the same level must have the same rules + its own.
 *
 * @exception MSP case considering all properties as True<bool>
 * for the Left side menu visibility.
 * Be careful with ! (not) operator.
 *
 *
 * @typedef {object} Pages
 * @infer
 */
const pages = {
  home: {
    dashboards: {
      mitigation: (l, p, i) =>
        checkPerm(p, [
          ROLE.ADMIN,
          ROLE.INCIDENT_OPERATOR,
          ROLE.INCIDENT_MANAGER,
          ROLE.INCIDENT_VIEWER,
        ]),
    },
  },
  awarenessCenter: {
    self: (l, p, i) => checkPerm(p, [ROLE.ADMIN, ROLE.CAMPAIGN_EDITOR]),
    phishing_simulator: {
      self: (l, p, i) =>
        checkPerm(p, [ROLE.ADMIN, ROLE.CAMPAIGN_EDITOR]) && atLeastCampaignsOnlyPlan(l.plan),
    },
    wizard_campaign: {
      // FIXME: TBD
      self: (l, p, i) => checkPerm(p, ['admin']) && i.o365,
    },
  },
  protectedMailboxes: {
    self: (l, p, i) => checkPerm(p, [ROLE.ADMIN]),
    segments: (l, p, i) => checkPerm(p, [ROLE.ADMIN]) && !exactStarterPlan(l.plan),
  },
  // Vendor Protection (IC-49943, moved out of Settings by IC-50849). Same roles
  // as impersonation protection, which the vendor registry feeds, behind the
  // VENDOR_BASE flag so the page ships dark and is switched on per company.
  vendorProtection: {
    self: (l, p, i, f) =>
      checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]) &&
      atLeastStarterPlan(l.plan) &&
      f.VENDOR_BASE,
  },
  settings: {
    account_settings: {
      general: (l, p, i) => checkPerm(p, [ROLE.ADMIN]),
      system_users: (l, p, i) => checkPerm(p, [ROLE.ADMIN]),
      whitelist: (l, p, i) => checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]),
      languages: (l, p, i) => checkPerm(p, [ROLE.ADMIN]),
      // billableAgent only exists for the self company (/account/status); partner logins and
      // MSP child tenants have none, and the backend treats a missing row as all-enabled
      // (webapp/rest/account_rest.py), so an undefined toggle must not deny access.
      ai_credits: (l, p, i, f, salesDemo, ba) =>
        checkPerm(p, [ROLE.ADMIN]) &&
        ((f.ENABLE_RED_TEAM && (ba?.redAgent ?? true)) ||
          (f.PSIMA_SERVICE && (ba?.psimaAgent ?? true)) ||
          (f.ENABLE_THEMIS_AI_ANALYSIS && (ba?.phishSoc ?? true))),
    },
    threats_protection: {
      self: (l, p, i) => checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]),
      incident_response: (l, p, i) =>
        checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]) && atLeastStarterPlan(l.plan),
      intelligence: (l, p, i) => checkPerm(p, [ROLE.ADMIN]) && atLeastStarterPlan(l.plan),
      account_takeover: (l, p, i) =>
        checkPerm(p, [ROLE.ADMIN]) && atLeastStarterPlan(l.plan) && i.o365,
      business_email_compromise: (l, p, i) =>
        checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]) && atLeastStarterPlan(l.plan),
      customization: (l, p, i) =>
        checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]) &&
        atLeastStarterPlan(l.plan) &&
        (i.exchange || !isIntegrated(i)),
      customizationO365: (l, p, i, f, salesDemo) =>
        (checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]) &&
          atLeastStarterPlan(l.plan) &&
          i.o365) ||
        salesDemo,
      microsoftQuarantine: (l, p, i, f) => checkPerm(p, [ROLE.ADMIN]) && i.o365,
    },

    simulator: {
      self: (l, p, i) => checkPerm(p, [ROLE.ADMIN, ROLE.CAMPAIGN_EDITOR]),
      psima: (l, p, i, f, salesDemo, ba) =>
        checkPerm(p, [ROLE.ADMIN, ROLE.CAMPAIGN_EDITOR]) && !!f.PSIMA_SERVICE && !!ba?.psimaAgent,
    },
    integrations: {
      self: (l, p, i) => checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]),
      alerts_and_logging: (l, p, i) =>
        checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]) && atLeastIronscalesProtectPlan(l.plan),
    },
    notifications: {
      self: (l, p, i) => checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]),
      collaboration_apps_alerts: (l, p, i) =>
        checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]) && atLeastStarterPlan(l.plan),
      system_alerts: (l, p, i) => checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]),
      end_user_alerts: (l, p, i) => checkPerm(p, [ROLE.ADMIN, ROLE.INCIDENT_MANAGER]),
    },
    deepFake: {
      self: (l, p, i) => checkPerm(p, [ROLE.ADMIN]),
      general: (l, p, i) => checkPerm(p, [ROLE.ADMIN]),
      integration: (l, p, i, f) => checkPerm(p, [ROLE.ADMIN]) && f.DEEPFAKE_SERVICE,
    },
  },
  groupSettings: {
    self: (l, p, i) => checkPerm(p, ['admin']),
  },
  resolutionCenter: (l, p, i) =>
    checkPerm(p, [
      ROLE.ADMIN,
      ROLE.INCIDENT_OPERATOR,
      ROLE.INCIDENT_MANAGER,
      ROLE.INCIDENT_VIEWER,
    ]) && atLeastStarterPlan(l.plan),
  activities: (l, p, i) => l.plan && !exactStarterPlan(l.plan) && checkPerm(p, [ROLE.ADMIN]),
}

/**
 * Valid if at least one of requested
 * permission is true.
 */
export function checkPerm(currentPerm, askingPermArray) {
  return !!askingPermArray.filter((perm) => currentPerm[perm]).length
}

function isIntegrated(i) {
  return !!Object.keys(i).filter((key) => i[key]).length
}

/**
 * Returns visibility status of items in the left side menu.
 *
 * For MSP case returns everything visible
 * since we do not have the all child's data (l, p, i) to check.
 * UX decision to have all items always visible on the menu,
 * then a page itself will do the relevant check
 * (since at the page step child data already loaded).
 * @param {keyof typeof pages} sectionName - The name of the section to check visibility for.
 * @param {{ partner: boolean, licenses: object, permissions: object, integrations: object, ff: object, isStaticSalesDemo: boolean, billableAgent: object }} props
 */
function getItemsVisibility(props, sectionName) {
  const { partner, licenses, permissions, integrations, ff, isStaticSalesDemo, billableAgent } =
    props

  const l = partner ? { ...licenses, plan: PLAN_TYPES.EMAIL_PROTECT } : licenses
  const p = partner ? setAllTrue(permissions) : permissions
  const i = partner ? setAllTrue(integrations) : integrations
  const f = partner ? setAllTrue(ff) : ff
  const ba = partner ? setAllTrue(billableAgent) : billableAgent
  const salesDemo = isStaticSalesDemo

  const itemsVisibility = {}

  const visibility = pages[sectionName]

  /**
   * Taking all pages[sectionName]
   * rules and execute them with (l, p, i, f)
   */

  Object.keys(visibility).forEach((topKey) => {
    if (typeof visibility[topKey] === 'function') {
      itemsVisibility[topKey] = visibility[topKey](l, p, i, f, salesDemo, ba)
    } else {
      itemsVisibility[topKey] = {}
      Object.keys(visibility[topKey]).forEach((lowKey) => {
        itemsVisibility[topKey][lowKey] = visibility[topKey][lowKey](l, p, i, f, salesDemo, ba)
      })
    }
  })

  return itemsVisibility
}

/**
 * Return the exact schema of @entity {object}
 * with property=True if at least one of the
 * child companies has its property set True
 */
function getCommon(props, entity) {
  const res = {}
  if (!props[entity]) throw new Error(`${entity} not in props`)
  Object.keys(props[entity]).forEach((key) => {
    res[key] = !!props.partner.companies_data.filter((c) => c[entity][key]).length
  })
  return res
}

/**
 * Return the exact schema of @entity {object}
 * filled with True
 */
function setAllTrue(entity = {}) {
  const res = {}
  Object.keys(entity).forEach((key) => {
    res[key] = true
  })
  return res
}

function epicCheck(store, checkPartnerAccess) {
  checkPartnerAccess()
}

export default {
  pages,
  epicCheck,
  getItemsVisibility,
  check: checkPerm,
}
